import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, CardBody, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Button } from 'reactstrap';

import { activateAuthLayout } from '../../../store/actions';
import { connect } from 'react-redux';
import { gql } from 'apollo-boost';
import { useQuery, useMutation } from '@apollo/react-hooks';
import Select from 'react-select';
import SweetAlert from 'react-bootstrap-sweetalert';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import env from '../../../../src/env';
import { useHistory } from 'react-router';
import {
  getQuoteBuilderURL,
  capitalizeStr,
  GROUP_COLORS,
  formatPrice,
  roundCurrency,
} from '../../../helpers/common';
import ColumnHeadersTooltip from '../../../components/ColumnHeadersTooltip';
import TooltipItem from '../../../components/TooltipItem';

const capexAssumedContractLength = Number(env.CAPEX_ASSUMED_CONTRACT_LENGTH);

/**
 * graphQL query for all the data needed for quote items UI
 * Quote - quote based from the passed quote id in the URL
 * Bundles, ProductCategories - list all the product for the live search
 * QuoteItems - query all the quote items of the current quote based on the URL
 **/
const DATA = gql`
  query Data($QuoteId: ID) {
    Quote(QuoteId: $QuoteId) {
      QuoteId
      Description
      QuoteTypeId1
      QuoteType1 {
        QuoteTypeId
        Name
      }
      QuoteTypeId2
      QuoteType2 {
        QuoteTypeId
        Name
      }
      QuoteContractMonths1
      QuoteContractMonths2
      Quote1DiscountMonthly
      Quote1DiscountTypeMonthly
      Quote1DiscountHardware
      Quote1DiscountTypeHardware
      Quote1DiscountSetup
      Quote1DiscountTypeSetup
      Quote2DiscountMonthly
      Quote2DiscountTypeMonthly
      Quote2DiscountHardware
      Quote2DiscountTypeHardware
      Quote2DiscountSetup
      Quote2DiscountTypeSetup
      WorkflowStep
      AcceptedQuote
      Status
      IsTemplate
      SummariseAllHardwareItemsQuote1
      SummariseAllHardwareItemsQuote2
      GroupItemsByCategory
      QuoteSites {
        QuoteSiteId
        Name
      }
    }

    Bundles {
      BundleId
      Name
      Description
      BundleItems {
        BundleItemId
        ProductId
        Quantity
        LinkQuoteItems
        Product {
          ProductId
          ProductCode
          Name
          Description
          HasProposalTemplate
        }
      }
    }

    ProductCategories {
      CatId
      Name
      Icon
    }

    ProductsByCategory {
      CatId
      Name
      Icon
      Products {
        ProductId
        ProductCode
        Name
        Description
        HasProposalTemplate
      }
    }

    QuoteItems(QuoteId: $QuoteId) {
      LineId
      QuoteId
      ProductId
      CatId
      Qty
      ItemGroupId
      PriceLineId
      OpexProration
      ShowInHardwareTable
      ShowProductDetails
      ShowQuantity
      Description
      ProductCode
      PriceType
      CostPrice
      AllowCostPriceOverride
      Price1
      Price2
      ComparisonLineItems
      ComparisonPrice
      QuoteSiteId
      QuoteSite {
        QuoteSiteId
        Name
      }
      Order
      ShowPriceQuote1
      ShowPriceQuote2
      DisplayProductQuote1
      DisplayProductQuote2
      IncludePriceInTotalQuote1
      IncludePriceInTotalQuote2
      Product {
        Name
        XeroTotalCostPool
        XeroQuantityOnHand
        HasProposalTemplate
      }
    }

    Settings(Type: "Admin Setting") {
      SettingId
      Value
      Type
    }
  }
`;

const UPDATE_QUOTE_ITEM = gql`
  mutation updateQuoteItem(
    $LineId: Int
    $Description: String
    $ProductCode: String
    $CatId: Int
    $PriceType: PriceType
    $CostPrice: Float
    $Price1: Float
    $Price2: Float
    $Qty: Int
    $ItemGroupId: String
    $ComparisonLineItems: String
    $ComparisonPrice: Float
    $QuoteSiteId: Int
    $Order: Int
    $ShowPriceQuote1: Int
    $ShowPriceQuote2: Int
    $DisplayProductQuote1: Int
    $DisplayProductQuote2: Int
    $IncludePriceInTotalQuote1: Int
    $IncludePriceInTotalQuote2: Int
    $ShowInHardwareTable: Int
    $ShowProductDetails: Int
    $ShowQuantity: Int
  ) {
    updateQuoteItem(
      LineId: $LineId
      Description: $Description
      ProductCode: $ProductCode
      CatId: $CatId
      PriceType: $PriceType
      CostPrice: $CostPrice
      Price1: $Price1
      Price2: $Price2
      Qty: $Qty
      ItemGroupId: $ItemGroupId
      ComparisonLineItems: $ComparisonLineItems
      ComparisonPrice: $ComparisonPrice
      QuoteSiteId: $QuoteSiteId
      Order: $Order
      ShowPriceQuote1: $ShowPriceQuote1
      ShowPriceQuote2: $ShowPriceQuote2
      DisplayProductQuote1: $DisplayProductQuote1
      DisplayProductQuote2: $DisplayProductQuote2
      IncludePriceInTotalQuote1: $IncludePriceInTotalQuote1
      IncludePriceInTotalQuote2: $IncludePriceInTotalQuote2
      ShowInHardwareTable: $ShowInHardwareTable
      ShowProductDetails: $ShowProductDetails
      ShowQuantity: $ShowQuantity
    ) {
      LineId
      QuoteId
      ProductId
      CatId
      Qty
      ItemGroupId
      PriceType
      CostPrice
      Price1
      Price2
      ComparisonLineItems
      ComparisonPrice
      QuoteSiteId
      Order
      ShowPriceQuote1
      ShowPriceQuote2
      DisplayProductQuote1
      DisplayProductQuote2
      IncludePriceInTotalQuote1
      IncludePriceInTotalQuote2
      ShowInHardwareTable
      ShowProductDetails
      ShowQuantity
      Description
      ProductCode
      Product {
        Name
      }
    }
  }
`;

const DELETE_QUOTE_ITEM = gql`
  mutation deleteQuoteItem($LineId: Int!) {
    deleteQuoteItem(LineId: $LineId) {
      LineId
      QuoteId
      ProductId
      Qty
      ItemGroupId
      PriceType
      CostPrice
      Price1
      Price2
      ComparisonLineItems
      ComparisonPrice
      QuoteSiteId
      Order
      Product {
        Name
      }
    }
  }
`;

const ADD_PRODUCT_TO_QUOTE = gql`
  mutation addProductToQuote($QuoteId: ID!, $ProductId: Int, $BundleId: ID) {
    addProductToQuote(QuoteId: $QuoteId, ProductId: $ProductId, BundleId: $BundleId) {
      LineId
      QuoteId
      ProductId
      CatId
      Qty
      ItemGroupId
      BundleId
      ShowInHardwareTable
      ShowProductDetails
      ShowQuantity
      Description
      ProductCode
      PriceType
      CostPrice
      Price1
      Price2
      ComparisonLineItems
      ComparisonPrice
      QuoteSiteId
      Order
      ShowPriceQuote1
      ShowPriceQuote2
      DisplayProductQuote1
      DisplayProductQuote2
      IncludePriceInTotalQuote1
      IncludePriceInTotalQuote2
      AllowCostPriceOverride
      Product {
        Name
      }
    }
  }
`;

const UPDATE_QUOTE_ITEMS = gql`
  mutation updateQuoteItems($QuoteItemsList: [QuoteItemsList]) {
    updateQuoteItems(input: $QuoteItemsList) {
      LineId
      QuoteId
      ProductId
      CatId
      Qty
      ItemGroupId
      ShowInHardwareTable
      ShowProductDetails
      ShowQuantity
      PriceType
      CostPrice
      Price1
      Price2
      ComparisonLineItems
      ComparisonPrice
      QuoteSiteId
      Order
      ShowPriceQuote1
      ShowPriceQuote2
      DisplayProductQuote1
      DisplayProductQuote2
      IncludePriceInTotalQuote1
      IncludePriceInTotalQuote2
      Product {
        Name
      }
    }
  }
`;


const UPDATE_QUOTE = gql`
  mutation updateQuote($QuoteInput: QuoteInput) {
    updateQuote(input: $QuoteInput) {
      QuoteId
      CompanyName
      Quote1DiscountMonthly
      Quote1DiscountTypeMonthly
      Quote1DiscountHardware
      Quote1DiscountTypeHardware
      Quote1DiscountSetup
      Quote1DiscountTypeSetup
      Quote2DiscountMonthly
      Quote2DiscountTypeMonthly
      Quote2DiscountHardware
      Quote2DiscountTypeHardware
      Quote2DiscountSetup
      Quote2DiscountTypeSetup
      WorkflowStep
      AcceptedQuote
      ProposalModified
      CustomerId
      SummariseAllHardwareItemsQuote1
      SummariseAllHardwareItemsQuote2
    }
  }
`;

const ADD_CUSTOM_QUOTE_ITEM = gql`
  mutation addCustomQuoteItem($QuoteId: ID!, $PriceType: PriceType!, $Description: String!, $ProductCode: String, $CatId: Int) {
    addCustomQuoteItem(QuoteId: $QuoteId, PriceType: $PriceType, Description: $Description, ProductCode: $ProductCode, CatId: $CatId) {
      LineId
      QuoteId
      ProductId
      CatId
      Qty
      ItemGroupId
      ShowInHardwareTable
      ShowProductDetails
      ShowQuantity
      Description
      ProductCode
      PriceType
      CostPrice
      AllowCostPriceOverride
      Price1
      Price2
      ComparisonLineItems
      ComparisonPrice
      QuoteSiteId
      Order
      ShowPriceQuote1
      ShowPriceQuote2
      DisplayProductQuote1
      DisplayProductQuote2
      IncludePriceInTotalQuote1
      IncludePriceInTotalQuote2
      Product {
        Name
      }
    }
  }
`;

const CREATE_QUOTE_SITE = gql`
  mutation createQuoteSite($QuoteId: Int!, $Name: String!) {
    createQuoteSite(QuoteId: $QuoteId, Name: $Name) {
      QuoteSiteId
      Name
    }
  }
`;

const UPDATE_QUOTE_SITE = gql`
  mutation updateQuoteSite($QuoteSiteId: Int!, $Name: String!) {
    updateQuoteSite(QuoteSiteId: $QuoteSiteId, Name: $Name) {
      QuoteSiteId
      Name
    }
  }
`;

const DELETE_QUOTE_SITE = gql`
  mutation deleteQuoteSite($QuoteSiteId: Int!) {
    deleteQuoteSite(QuoteSiteId: $QuoteSiteId) {
      QuoteSiteId
    }
  }
`;

const buildGroupColorMap = (quoteItems) => {
  if (!quoteItems?.length) return {};
  const counts = {};
  quoteItems.forEach(i => { if (i.ItemGroupId) counts[i.ItemGroupId] = (counts[i.ItemGroupId] || 0) + 1; });
  const map = {};
  let idx = 0;
  [...quoteItems].sort((a, b) => a.LineId - b.LineId).forEach(i => {
    if (i.ItemGroupId && counts[i.ItemGroupId] >= 2 && !(i.ItemGroupId in map)) {
      map[i.ItemGroupId] = GROUP_COLORS[idx % GROUP_COLORS.length];
      idx++;
    }
  });
  return map;
};

const QUOTE_ITEMS_COLUMN_HEADERS = [
  {
    id: 1,
    field: 'Name',
    label: 'Quote Item',
    description: 'The name and product code of the line item',
    minWidth: '320px',
  },
  {
    id: 2,
    field: 'Qty',
    label: 'Quantity',
    description: 'The number of units of this line item',
    width: '15%',
    minWidth: '140px',
  },
  {
    id: 10,
    field: 'CostPrice',
    label: 'Cost Price',
    description: 'The worst-case cost price of this line item',
    width: '10%',
    minWidth: '100px',
  },
  {
    id: 4,
    field: 'Price1',
    label: 'Quote 1 Price',
    description: 'The price for this line item for the first quote option',
    quoteType: true,
    width: '15%',
    minWidth: '140px',
  },
  {
    id: 5,
    field: 'Price2',
    label: 'Quote 2 Price',
    description: 'The price for this line item for the second quote option',
    quoteType: true,
    width: '15%',
    minWidth: '140px',
  },
  {
    id: 6,
    field: 'ComparisonLineItems',
    label: 'Comp Line Item',
    description: "The line item on a competitor's quote or bill to which this line should be compared",
    width: '15%',
    minWidth: '140px',
  },
  {
    id: 7,
    field: 'ComparisonPrice',
    label: 'Comp Price',
    description: "The competitor's prices for the comparison line item",
    width: '10%',
    minWidth: '100px',
  },
  {
    id: 8,
    field: 'Site',
    label: 'Site',
    description: 'If this quote includes multiple sites, select the site with which this line is associated',
    width: '15%',
    minWidth: '140px',
  },
];

const QuoteBuilderItems = ({ activateAuthLayout, ...props }) => {
  useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);

  let history = useHistory();

  // initialize states
  const [stepError, setStepError] = useState(null);
  const PRICE_TYPES = useMemo(() => ['monthly', 'hardware', 'setup'], []); // price type of products
  const QUOTE_TYPES_BADGE_CLASSES = {
    CAPEX: 'badge-primary',
    OPEX: 'badge-info',
    ENERDS: 'badge-danger',
  };
  const [groupOptions, setGroupOptions] = useState(null); // options/items on the live search
  const [quoteItems, setQuoteItems] = useState(null); // store the queried data here
  const [quoteSites, setQuoteSites] = useState([]);
  const [siteModal, setSiteModal] = useState({ open: false, mode: 'create', name: '', siteId: null, lineId: null });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quoteDiscounts, setQuoteDiscounts] = useState(null);
  // initialize apollo graphql queries and mutations
  const { data, loading } = useQuery(DATA, {
    variables: { QuoteId: props.quoteId },
  });
  const adminSettings = data && data.Settings ? JSON.parse(data.Settings.Value) : {};
  const minimumQuoteMargin = Number(adminSettings.MINIMUM_QUOTE_MARGIN || env.MINIMUM_QUOTE_MARGIN);
  const targetQuoteMargin = Number(adminSettings.TARGET_QUOTE_MARGIN || env.TARGET_QUOTE_MARGIN);
  const xeroPullEnabled = !!adminSettings.XERO_PULL_ENABLED;
  const groupItemsByCategory = !!data?.Quote?.GroupItemsByCategory;
  const [updateQuoteItem] = useMutation(UPDATE_QUOTE_ITEM);
  const [deleteQuoteItem] = useMutation(DELETE_QUOTE_ITEM);
  const [updateQuoteItems] = useMutation(UPDATE_QUOTE_ITEMS);
  const [updateQuote] = useMutation(UPDATE_QUOTE);
  const [addProductToQuote, { loading: addingToQuote }] = useMutation(ADD_PRODUCT_TO_QUOTE);
  const [addCustomQuoteItem] = useMutation(ADD_CUSTOM_QUOTE_ITEM);
  const [createQuoteSite] = useMutation(CREATE_QUOTE_SITE);
  const [updateQuoteSite] = useMutation(UPDATE_QUOTE_SITE);
  const [deleteQuoteSite] = useMutation(DELETE_QUOTE_SITE);
  const [itemModal, setItemModal] = useState(null);
  const [itemForm, setItemForm] = useState({ Description: '', ProductCode: '', CatId: null });
  const categoryOptions = useMemo(
    () => (data?.ProductCategories || []).map(c => ({ value: c.CatId, label: c.Name, icon: c.Icon })),
    [data?.ProductCategories]
  );
  const isTemplate = !!data?.Quote?.IsTemplate;
  const [disableQuote, setDisableQuote] = useState(false);
  const [SummariseAllHardwareItemsQuote1, setSummariseAllHardwareItemsQuote1] = useState(null);
  const [SummariseAllHardwareItemsQuote2, setSummariseAllHardwareItemsQuote2] = useState(null);

  const onChangeQuoteDiscountTotal = (e, field) => {
    const quote = {};
    quote['QuoteId'] = data.Quote.QuoteId;

    let value = e.value ?? e.target.value;

    switch (field) {
      case 'Quote1DiscountHardware':
      case 'Quote2DiscountHardware':
      case 'Quote1DiscountSetup':
      case 'Quote2DiscountSetup':
      case 'Quote1DiscountMonthly':
      case 'Quote2DiscountMonthly':
        quote[field] = parseFloat(value) || null;
        break;
      default:
        quote[field] = e.value ? e.value : e.target.value;
        break;
    }
 
    updateQuote({
      variables: {
        QuoteInput: quote,
      },
      refetchQueries: ['getCurrentQuote'],
    });
    setQuoteDiscounts({
      ...quoteDiscounts,
      [field]: quote[field],
    });
  };

  const calculateDiscount = (n, priceType, total) => {
    if (groupItemsByCategory) return total;
    const type = quoteDiscounts[`Quote${n}DiscountType${priceType}`];
    const discount = quoteDiscounts[`Quote${n}Discount${priceType}`];
    return total - (type === 'DOLLAR' ? discount : total * (discount / 100));
  };

  const calculateQuoteTotals = (n) => {
    const subtotals = { monthly: 0, hardware: 0, setup: 0 };
    quoteItems.forEach((item) => {
      const qty = item.Qty ?? 0;
      if (item[`IncludePriceInTotalQuote${n}`]) {
        subtotals[item.PriceType] += roundCurrency((item[`Price${n}`] ?? 0) * qty);
      }
    });
    return Object.fromEntries(
      Object.entries(subtotals).map(([type, subtotal]) => [
        type,
        { subtotal, total: calculateDiscount(n, capitalizeStr(type), subtotal) },
      ])
    );
  };

  const calculateQuoteCosts = () => {
    let totalCosts = {
      monthly: 0,
      hardware: 0,
      setup: 0,
    };
    quoteItems.forEach((item) => {
      totalCosts[item.PriceType] += roundCurrency(item.CostPrice ? item.CostPrice * item.Qty : 0);
    });
    return totalCosts;
  };

  const calculateGrossMargin = (price, cost) => {
    let marginPercentage = price > 0 ? ((price - cost) / price) * 100 : 0;
    marginPercentage = Math.round(marginPercentage);
    if (!isFinite(marginPercentage)) {
      marginPercentage = 0;
    }

    return marginPercentage;
  };

  const formatMargin = (price, cost) => {
    const marginNumber = formatPrice(price - cost);
    let marginPercentage = calculateGrossMargin(price, cost);
    return `${marginNumber} (${marginPercentage}%)`;
  };

  const calculateContractMargins = () => {
    const costs = calculateQuoteCosts();
    const initialExpenses = costs.hardware + costs.setup;

    const calculatePayback = (initial, monthly) => {
      const netInitial = initialExpenses - initial;
      if (netInitial <= 0) return 0;
      const monthlyMargin = monthly - costs.monthly;
      if (monthlyMargin <= 0) return null;
      return netInitial / monthlyMargin;
    };

    return [1, 2].map((n) => {
      const totals = calculateQuoteTotals(n);
      const contractPeriod = parseInt(data.Quote[`QuoteContractMonths${n}`] || capexAssumedContractLength);
      const totalExpenses = initialExpenses + costs.monthly * contractPeriod;
      const initialRevenue = totals.hardware.total + totals.setup.total;
      const monthlyRevenue = totals.monthly.total;
      const totalRevenue = initialRevenue + monthlyRevenue * contractPeriod;
      const margin = totalRevenue - totalExpenses;
      const payback = calculatePayback(initialRevenue, monthlyRevenue);
      return {
        contractPeriod,
        totalExpenses,
        initialRevenue,
        monthlyRevenue,
        totalRevenue,
        margin,
        marginPct: totalRevenue > 0 ? Math.round((margin / totalRevenue) * 100) : 0,
        payback: payback !== null ? `${payback.toFixed(1)} months` : '—',
      };
    });
  };

  const quoteBadge = (name) => (
    <span className={`badge badge-pill ${QUOTE_TYPES_BADGE_CLASSES[name]}`}>
      {name}
    </span>
  );

  /**
   * Add new quote item when user select a product with product prices based on the quote quote types
   */
  const selectProduct = (prod) => {
    const variables = { QuoteId: props.quoteId };
    if (prod.items) {
      variables.BundleId = prod.value;
    } else {
      variables.ProductId = prod.value;
    }
    addProductToQuote({
      variables,
      refetchQueries: ['Data', 'getCurrentQuote'],
    });
    setSelectedProduct(null);
  };

  /**
   * Update the quoteItem state when user update the fields of the quote items
   */
  const updateQuoteItemField = (e, field, lineId) => {
    const intFields = [
      'ShowPriceQuote1',
      'ShowPriceQuote2',
      'DisplayProductQuote1',
      'DisplayProductQuote2',
      'IncludePriceInTotalQuote1',
      'IncludePriceInTotalQuote2',
      'ShowInHardwareTable',
      'ShowProductDetails',
      'ShowQuantity',
      'QuoteSiteId',
      'CatId',
    ];
    const floatFields = ['CostPrice', 'Price1', 'Price2', 'ComparisonPrice'];
    const propagatedFields = ['Description', 'CatId', 'Qty', 'QuoteSiteId', 'ShowProductDetails'];

    let fieldValue = intFields.includes(field) ? e : (e.target ? e.target.value : e.value);
    if (floatFields.includes(field)) fieldValue = fieldValue !== '' ? parseFloat(fieldValue) : null;
    if (field === 'Qty') fieldValue = fieldValue !== '' ? parseInt(fieldValue, 10) : null;

    const index = quoteItems.findIndex(item => item.LineId === lineId);
    const newItem = { ...quoteItems[index], [field]: fieldValue };

    updateQuoteItem({ variables: newItem, refetchQueries: ['getCurrentQuote'] });

    const shouldPropagate = (sibling) => {
      if (sibling.ItemGroupId !== newItem.ItemGroupId) return false;
      if (field === 'Description' && sibling.PriceLineId !== newItem.PriceLineId) return false;
      if (field === 'CatId' && sibling.ProductId !== newItem.ProductId) return false;
      return true;
    };

    if (propagatedFields.includes(field) && newItem.ItemGroupId) {
      quoteItems.forEach((sibling, i) => {
        if (i !== index && shouldPropagate(sibling))
          updateQuoteItem({ variables: { ...sibling, [field]: fieldValue } });
      });
    }

    setQuoteItems(prev => {
      const idx = prev.findIndex(item => item.LineId === lineId);
      if (idx === -1) return prev;
      const newArr = [...prev];
      newArr[idx] = { ...prev[idx], [field]: fieldValue };
      if (propagatedFields.includes(field) && newArr[idx].ItemGroupId) {
        prev.forEach((sibling, i) => {
          if (i !== idx && shouldPropagate(sibling))
            newArr[i] = { ...sibling, [field]: fieldValue };
        });
      }
      return newArr;
    });
  };

  /**
   * delete the quote item entry, also in the DB
   */
  const deleteQuoteItemEntry = (e, lineId) => {
    e.preventDefault();
    const item = quoteItems.find(i => i.LineId === lineId);
    const sibling = quoteItems.find(i =>
      i.LineId !== lineId &&
      i.ItemGroupId && i.ItemGroupId === item?.ItemGroupId &&
      i.PriceLineId && i.PriceLineId === item?.PriceLineId
    );
    const toDelete = [lineId, ...(sibling ? [sibling.LineId] : [])];
    setQuoteItems(prev => prev.filter(i => !toDelete.includes(i.LineId)));
    toDelete.forEach(id => deleteQuoteItem({ variables: { LineId: id }, refetchQueries: ['getCurrentQuote'] }));
  };

  const handleSiteModalSave = (e) => {
    e?.preventDefault();
    if (!siteModal.name.trim()) return;
    if (siteModal.mode === 'create') {
      createQuoteSite({
        variables: { QuoteId: parseInt(data.Quote.QuoteId), Name: siteModal.name.trim() },
      }).then(({ data: result }) => {
        const newSite = result.createQuoteSite;
        setQuoteSites(prev => [...prev, newSite]);
        updateQuoteItemField(newSite.QuoteSiteId, 'QuoteSiteId', siteModal.lineId);
        setSiteModal({ open: false, mode: 'create', name: '', siteId: null, lineId: null });
      });
    } else {
      updateQuoteSite({
        variables: { QuoteSiteId: siteModal.siteId, Name: siteModal.name.trim() },
      }).then(({ data: result }) => {
        const updated = result.updateQuoteSite;
        setQuoteSites(prev => prev.map(s => s.QuoteSiteId === updated.QuoteSiteId ? updated : s));
        setSiteModal({ open: false, mode: 'create', name: '', siteId: null, lineId: null });
      });
    }
  };

  const handleItemModalSave = (e) => {
    e?.preventDefault();
    if (!itemForm.Description.trim()) return;
    if (itemModal.mode === 'add') {
      addCustomQuoteItem({
        variables: {
          QuoteId: data.Quote.QuoteId,
          PriceType: itemModal.priceType,
          Description: itemForm.Description.trim(),
          ProductCode: itemForm.ProductCode.trim() || null,
          CatId: itemForm.CatId,
        },
      }).then(({ data: result }) => {
        setQuoteItems([...quoteItems, result.addCustomQuoteItem]);
        setItemModal(null);
      });
    } else {
      updateQuoteItemField({ value: itemForm.Description.trim() }, 'Description', itemModal.item.LineId);
      if (!itemModal.item.ProductId)
        updateQuoteItemField({ value: itemForm.ProductCode.trim() || null }, 'ProductCode', itemModal.item.LineId);
      updateQuoteItemField(itemForm.CatId, 'CatId', itemModal.item.LineId);
      setItemModal(null);
    }
  };

  const goToPreviousStep = (e) => {
    e.preventDefault();
    updateQuote({
      variables: {
        QuoteInput: {
          QuoteId: data.Quote.QuoteId,
          WorkflowStep: 'QUOTE_SETUP',
        },
      },
    }).then(() => {
      const dest = data.Quote.IsTemplate
        ? `/template-setup/${data.Quote.QuoteId}`
        : `/quote-setup/${data.Quote.QuoteId}`;
      history.replace(dest);
    });
  };

  const proceedNextStep = (e) => {
    e.preventDefault();
    checkMinMarginPercentage();
  };

  const checkMinMarginPercentage = () => {
    const [q1, q2] = calculateContractMargins();

    if (data.Quote.QuoteTypeId1 && q1.marginPct < minimumQuoteMargin) {
      setStepError({
        error: true,
        marginError: true,
        message: `The total margin on Quote 1 is below the minimum margin of ${minimumQuoteMargin}%.`,
      });
    } else if (data.Quote.QuoteTypeId2 && q2.marginPct < minimumQuoteMargin) {
      setStepError({
        error: true,
        marginError: true,
        message: `The total margin on Quote 2 is below the minimum margin of ${minimumQuoteMargin}%.`,
      });
    } else {
      updateQuoteStep();
    }
  };

  const updateQuoteStep = () => {
    updateQuote({
      variables: {
        QuoteInput: {
          QuoteId: data.Quote.QuoteId,
          WorkflowStep: 'QUOTE_RATES',
          SummariseAllHardwareItemsQuote1:
            SummariseAllHardwareItemsQuote1 !== null
              ? SummariseAllHardwareItemsQuote1
              : data.Quote.SummariseAllHardwareItemsQuote1,
          SummariseAllHardwareItemsQuote2:
            SummariseAllHardwareItemsQuote2 !== null
              ? SummariseAllHardwareItemsQuote2
              : data.Quote.SummariseAllHardwareItemsQuote2,
        },
      },
    }).then(() => {
      history.replace(`/quote-rates/${data.Quote.QuoteId}`);
    });
  };


  const onDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination || source.index === destination.index) {
      return;
    }

    const section = source.droppableId;
    const sectionItems = [...quoteItems]
      .filter(item => item.PriceType === section)
      .sort((a, b) => a.Order - b.Order);

    const [removed] = sectionItems.splice(source.index, 1);
    sectionItems.splice(destination.index, 0, removed);

    const reordered = quoteItems.map(item => {
      if (item.PriceType !== section) return item;
      return { ...item, Order: sectionItems.findIndex(si => si.LineId === item.LineId) };
    });

    setQuoteItems(reordered);
    updateQuoteItems({
      variables: {
        QuoteItemsList: reordered.map(({ Product, QuoteSite, ...item }) => item),
      },
      refetchQueries: ['getCurrentQuote'],
    });
  };

  const getItemStyle = (isDragging, draggableStyle) => ({
    userSelect: 'none',
    background: isDragging ? '#f0f4f7' : '#fff',
    ...draggableStyle,
  });

  /**
   * Render the tables with quote items based on the price type
   */
  const renderTables = (priceType, i) => {
    const groupColorMap = buildGroupColorMap(quoteItems);
    return (
      <Card key={priceType}>
        <CardBody>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable key={i} droppableId={priceType}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  className="table-responsive"
                  key={priceType}
                >
                  <h5
                    className="mt-0 mb-3"
                    style={{
                      textTransform: 'capitalize',
                    }}
                  >
                    {priceType}
                  </h5>
                  {data && data.Quote && quoteItems && (
                    <table className="table baseline mb-0" key={i}>
                      <thead className="table-dark">
                        <tr className="text-nowrap">
                          <th width="5" className="p-0">&nbsp;</th>
                          {QUOTE_ITEMS_COLUMN_HEADERS.map((column) => (
                            <ColumnHeadersTooltip
                              key={column.id}
                              id={'product-pricing'}
                              name={priceType}
                              column={column}
                              quoteType1={data.Quote.QuoteType1?.Name}
                              quoteType2={data.Quote.QuoteType2?.Name}
                            />
                          ))}
                          <th style={{ minWidth: '100px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.Quote &&
                        quoteItems &&
                        quoteItems.filter((quote) => quote.PriceType === priceType)
                          .length > 0 ? (
                          quoteItems.sort((a, b) => (a.Order > b.Order ? 1 : -1)) &&
                          quoteItems
                            .filter((quote) => quote.PriceType === priceType)
                            .map((item, index) => {
                              const currentSite = quoteSites.find(s => s.QuoteSiteId === item.QuoteSiteId);
                              return (
                              <Draggable
                                key={item.LineId}
                                draggableId={String(item.LineId)}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <tr
                                    key={item.LineId}
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    style={getItemStyle(
                                      snapshot.isDragging,
                                      provided.draggableProps.style
                                    )}
                                  >
                                    <td className="p-0" style={{ backgroundColor: groupColorMap[item.ItemGroupId] ?? '#dee2e6' }} />
                                    <td>
                                      <div className="d-flex align-items-baseline">
                                        <i
                                          className="fas fa-grip-vertical mr-2"
                                          style={{ color: "#bbb" }}
                                          {...provided.dragHandleProps}
                                        ></i>
                                        <div className="d-flex flex-column">
                                          <span>
                                            {item.Description}
                                            <button
                                              className="btn btn-link btn-sm p-0 ml-2"
                                              style={{ color: '#bbb', lineHeight: 1, verticalAlign: 'baseline' }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setItemForm({ Description: item.Description ?? '', ProductCode: item.ProductCode ?? '', CatId: item.CatId ?? null });
                                                setItemModal({ mode: 'edit', item });
                                              }}
                                            >
                                              <i className="fas fa-pencil"></i>
                                            </button>
                                          </span>
                                          {item.ProductCode && <span className="text-muted">{item.ProductCode}</span>}
                                          {groupItemsByCategory && (() => {
                                            const cat = categoryOptions.find(o => o.value === item.CatId);
                                            return (
                                              <span className="mt-auto text-muted">
                                                <i className={`fas ${cat?.icon ?? 'fa-cubes'} mr-1`} />
                                                {cat?.label ?? 'Other'}
                                              </span>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        value={item.Qty ?? ''}
                                        className={`form-control${item.Qty == null ? ' is-invalid' : ''}`}
                                        onChange={(e) => {
                                          updateQuoteItemField(e, 'Qty', item.LineId);
                                        }}
                                      />
                                      <div className="display-checkboxes">
                                        {priceType === 'hardware' && (
                                          <label className="display-checkbox-wrapper">
                                            <input
                                              type="checkbox"
                                              defaultChecked={item.ShowInHardwareTable}
                                              onChange={(e) =>
                                                updateQuoteItemField(
                                                  e.target.checked ? 1 : 0,
                                                  'ShowInHardwareTable',
                                                  item.LineId
                                                )
                                              }
                                            />
                                            <span className="display-checkbox-text">List as hardware item</span>
                                          </label>
                                        )}
                                        {item.Product?.HasProposalTemplate === 1 && (
                                          <label className="display-checkbox-wrapper">
                                            <input
                                              type="checkbox"
                                              checked={!!item.ShowProductDetails}
                                              onChange={(e) =>
                                                updateQuoteItemField(
                                                  e.target.checked ? 1 : 0,
                                                  'ShowProductDetails',
                                                  item.LineId
                                                )
                                              }
                                            />
                                            <span className="display-checkbox-text">Show product details</span>
                                          </label>
                                        )}
                                        <label className="display-checkbox-wrapper">
                                          <input
                                            type="checkbox"
                                            checked={!!item.ShowQuantity}
                                            onChange={(e) =>
                                              updateQuoteItemField(
                                                e.target.checked ? 1 : 0,
                                                'ShowQuantity',
                                                item.LineId
                                              )
                                            }
                                          />
                                          <span className="display-checkbox-text">Show quantity</span>
                                        </label>
                                      </div>
                                    </td>
                                    <td className="text-right">
                                      {item.AllowCostPriceOverride ? (
                                        <input
                                          type="number"
                                          defaultValue={item.CostPrice}
                                          className={`form-control text-right${!isTemplate && item.CostPrice == null ? ' is-invalid' : ''}`}
                                          readOnly={isTemplate}
                                          onChange={(e) =>
                                            updateQuoteItemField(
                                              e,
                                              'CostPrice',
                                              item.LineId
                                            )
                                          }
                                        />
                                      ) : (
                                        formatPrice(item.CostPrice)
                                      )}
                                      {!!(xeroPullEnabled &&
                                        item.Product?.XeroTotalCostPool &&
                                        item.Product?.XeroQuantityOnHand) && (
                                          <TooltipItem
                                            key={`tooltip-${index}`}
                                            id={`xero-total-cost-pool-${item.LineId}`}
                                            label={``}
                                            description={(() => {
                                              let average_buy_price = '';
                                              if (
                                                item.Product?.XeroTotalCostPool &&
                                                item.Product?.XeroQuantityOnHand
                                              ) {
                                                average_buy_price = formatPrice(
                                                  parseFloat(
                                                    item.Product.XeroTotalCostPool
                                                  ) /
                                                  parseFloat(
                                                    item.Product.XeroQuantityOnHand
                                                  )
                                                );
                                              }
                                              return (
                                                <div>
                                                  The average cost price of this item
                                                  recently is {average_buy_price}
                                                </div>
                                              );
                                            })()}
                                          />
                                        )}
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        {...(isTemplate ? { value: item.Price1 } : { defaultValue: item.Price1 })}
                                        className="form-control text-right"
                                        disabled={!data.Quote.QuoteTypeId1}
                                        readOnly={isTemplate}
                                        onChange={(e) =>
                                          updateQuoteItemField(
                                            e,
                                            'Price1',
                                            item.LineId
                                          )
                                        }
                                      />
                                      <div className="line-total">
                                        <span>
                                          = {formatPrice(item.Price1 * item.Qty)}
                                        </span>
                                      </div>
                                      <div className="display-checkboxes">
                                        <label className="display-checkbox-wrapper">
                                          <input
                                            type="checkbox"
                                            defaultChecked={item.DisplayProductQuote1}
                                            disabled={!data.Quote.QuoteTypeId1}
                                            onChange={(e) =>
                                              updateQuoteItemField(
                                                e.target.checked ? 1 : 0,
                                                'DisplayProductQuote1',
                                                item.LineId
                                              )
                                            }
                                          />
                                          <span className="display-checkbox-text">Display product</span>
                                        </label>
                                        <label className="display-checkbox-wrapper">
                                          <input
                                            type="checkbox"
                                            defaultChecked={item.ShowPriceQuote1}
                                            disabled={!data.Quote.QuoteTypeId1}
                                            onChange={(e) =>
                                              updateQuoteItemField(
                                                e.target.checked ? 1 : 0,
                                                'ShowPriceQuote1',
                                                item.LineId
                                              )
                                            }
                                          />
                                          <span className="display-checkbox-text">Display price</span>
                                        </label>
                                        <label className="display-checkbox-wrapper">
                                          <input
                                            type="checkbox"
                                            defaultChecked={
                                              item.IncludePriceInTotalQuote1
                                            }
                                            disabled={!data.Quote.QuoteTypeId1}
                                            onChange={(e) =>
                                              updateQuoteItemField(
                                                e.target.checked ? 1 : 0,
                                                'IncludePriceInTotalQuote1',
                                                item.LineId
                                              )
                                            }
                                          />
                                          <span className="display-checkbox-text">Include in total</span>
                                        </label>
                                      </div>
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        {...(isTemplate ? { value: item.Price2 } : { defaultValue: item.Price2 })}
                                        className="form-control text-right"
                                        disabled={!data.Quote.QuoteTypeId2}
                                        readOnly={isTemplate}
                                        onChange={(e) =>
                                          updateQuoteItemField(
                                            e,
                                            'Price2',
                                            item.LineId
                                          )
                                        }
                                      />
                                      <div className="line-total">
                                        <span>
                                          = {formatPrice(item.Price2 * item.Qty)}
                                        </span>
                                      </div>
                                      <div className="display-checkboxes">
                                        <label className="display-checkbox-wrapper">
                                          <input
                                            type="checkbox"
                                            defaultChecked={item.DisplayProductQuote2}
                                            disabled={!data.Quote.QuoteTypeId2}
                                            onChange={(e) =>
                                              updateQuoteItemField(
                                                e.target.checked ? 1 : 0,
                                                'DisplayProductQuote2',
                                                item.LineId
                                              )
                                            }
                                          />
                                          <span className="display-checkbox-text">Display product</span>
                                        </label>
                                        <label className="display-checkbox-wrapper">
                                          <input
                                            type="checkbox"
                                            defaultChecked={item.ShowPriceQuote2}
                                            disabled={!data.Quote.QuoteTypeId2}
                                            onChange={(e) =>
                                              updateQuoteItemField(
                                                e.target.checked ? 1 : 0,
                                                'ShowPriceQuote2',
                                                item.LineId
                                              )
                                            }
                                          />
                                          <span className="display-checkbox-text">Display price</span>
                                        </label>
                                        <label className="display-checkbox-wrapper">
                                          <input
                                            type="checkbox"
                                            defaultChecked={
                                              item.IncludePriceInTotalQuote2
                                            }
                                            disabled={!data.Quote.QuoteTypeId2}
                                            onChange={(e) =>
                                              updateQuoteItemField(
                                                e.target.checked ? 1 : 0,
                                                'IncludePriceInTotalQuote2',
                                                item.LineId
                                              )
                                            }
                                          />
                                          <span className="display-checkbox-text">Include in total</span>
                                        </label>
                                      </div>
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        defaultValue={item.ComparisonLineItems}
                                        className="form-control"
                                        readOnly={isTemplate}
                                        onChange={(e) =>
                                          updateQuoteItemField(
                                            e,
                                            'ComparisonLineItems',
                                            item.LineId
                                          )
                                        }
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        defaultValue={item.ComparisonPrice}
                                        className="form-control text-right"
                                        readOnly={isTemplate}
                                        onChange={(e) =>
                                          updateQuoteItemField(
                                            e,
                                            'ComparisonPrice',
                                            item.LineId
                                          )
                                        }
                                      />
                                    </td>
                                    <td>
                                      <Select
                                        options={[
                                          { value: null, label: 'None', isMeta: true },
                                          ...quoteSites.map(s => ({ value: s.QuoteSiteId, label: s.Name })),
                                          { value: 'add', label: 'Add new site', isMeta: true },
                                          ...(item.QuoteSiteId ? [
                                            { value: 'rename', label: `Rename '${currentSite?.Name}'`, isMeta: true },
                                            { value: 'delete', label: `Delete '${currentSite?.Name}'`, isMeta: true },
                                          ] : []),
                                        ]}
                                        value={item.QuoteSiteId
                                          ? { value: item.QuoteSiteId, label: currentSite?.Name }
                                          : { value: null, label: 'None', isMeta: true }
                                        }
                                        isDisabled={isTemplate}
                                        isSearchable={false}
                                        menuPortalTarget={document.body}
                                        styles={{
                                          option: (base, { data, isSelected }) => ({ ...base, color: data.isMeta && !isSelected ? '#aaa' : base.color, whiteSpace: 'nowrap' }),
                                          singleValue: (base, { data }) => ({ ...base, color: data.isMeta ? '#aaa' : base.color }),
                                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                          menu: (base) => ({ ...base, width: 'max-content', minWidth: '100%' }),
                                        }}
                                        onChange={(selected) => {
                                          if (selected.value === 'add') {
                                            setSiteModal({ open: true, mode: 'create', name: '', siteId: null, lineId: item.LineId });
                                          } else if (selected.value === 'rename') {
                                            setSiteModal({ open: true, mode: 'rename', name: currentSite?.Name ?? '', siteId: item.QuoteSiteId, lineId: item.LineId });
                                          } else if (selected.value === 'delete') {
                                            const siteId = item.QuoteSiteId;
                                            deleteQuoteSite({ variables: { QuoteSiteId: siteId } }).then(() => {
                                              setQuoteSites(prev => prev.filter(s => s.QuoteSiteId !== siteId));
                                              setQuoteItems(prev => prev.map(qi => qi.QuoteSiteId === siteId ? { ...qi, QuoteSiteId: null, QuoteSite: null } : qi));
                                            });
                                          } else {
                                            updateQuoteItemField(selected.value, 'QuoteSiteId', item.LineId);
                                          }
                                        }}
                                      />
                                    </td>
                                    <td>
                                      <button
                                        className="btn btn-danger"
                                        onClick={(e) => deleteQuoteItemEntry(e, item.LineId)}
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                )}
                              </Draggable>
                              );
                            })
                        ) : (
                          <tr key={0}>
                            <td colSpan={10} className="text-center">
                              No {priceType} quote items
                            </td>
                          </tr>
                        )}
                        {priceType.toLowerCase() === 'hardware' && (
                          <tr>
                            <td colSpan={4}></td>
                            <td>
                              <label className="display-checkbox-wrapper">
                                <input
                                  type="checkbox"
                                  checked={!!SummariseAllHardwareItemsQuote1}
                                  disabled={!data.Quote.QuoteTypeId1}
                                  onChange={(e) => {
                                    const val = +e.target.checked;
                                    setSummariseAllHardwareItemsQuote1(val);
                                    updateQuote({
                                      variables: { QuoteInput: { QuoteId: data.Quote.QuoteId, SummariseAllHardwareItemsQuote1: val } },
                                      refetchQueries: ['getCurrentQuote']
                                    });
                                  }}
                                />
                                <span className="display-checkbox-text">Single hardware line item</span>
                              </label>
                            </td>
                            <td>
                              <label className="display-checkbox-wrapper">
                                <input
                                  type="checkbox"
                                  checked={!!SummariseAllHardwareItemsQuote2}
                                  disabled={!data.Quote.QuoteTypeId2}
                                  onChange={(e) => {
                                    const val = +e.target.checked;
                                    setSummariseAllHardwareItemsQuote2(val);
                                    updateQuote({
                                      variables: { QuoteInput: { QuoteId: data.Quote.QuoteId, SummariseAllHardwareItemsQuote2: val } },
                                      refetchQueries: ['getCurrentQuote']
                                    });
                                  }}
                                />
                                <span className="display-checkbox-text">Single hardware line item</span>
                              </label>
                            </td>
                            <td colSpan={4}></td>
                          </tr>
                        )}
                        <tr className="tr-subtotal">
                          <td colSpan={3}>
                            <span className="text-capitalize">{priceType}</span> Subtotal
                          </td>
                          <td className="text-right">
                            {formatPrice(calculateQuoteCosts()[priceType])}
                          </td>
                          <td className="text-right">
                            {formatPrice(calculateQuoteTotals(1)[priceType].subtotal)}
                          </td>
                          <td className="text-right">
                            {formatPrice(calculateQuoteTotals(2)[priceType].subtotal)}
                          </td>
                          <td colSpan={4} className="text-right p-0">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => {
                                setItemForm({ Description: '', ProductCode: '', CatId: null });
                                setItemModal({ mode: 'add', priceType });
                              }}
                            >
                              Add custom {priceType} item
                            </button>
                          </td>
                        </tr>
                        {!groupItemsByCategory && (
                          <tr className="tr-discount">
                            <td colSpan={4}>
                              <span className="text-capitalize">{priceType}</span>{' '}
                              Discount
                            </td>
                            <td className="text-right">
                              <div className="flex-container">
                                <select
                                  className="form-control discount-type-select"
                                  defaultValue={
                                    data.Quote[
                                      `Quote1DiscountType${capitalizeStr(priceType)}`
                                    ]
                                  }
                                  disabled={!data.Quote.QuoteTypeId1}
                                  onChange={(e) =>
                                    onChangeQuoteDiscountTotal(
                                      e,
                                      `Quote1DiscountType${capitalizeStr(priceType)}`
                                    )
                                  }
                                >
                                  <option value="DOLLAR">$</option>
                                  <option value="PERCENT">%</option>
                                </select>
                                <input
                                  type="number"
                                  className="form-control discount-input text-right"
                                  defaultValue={
                                    data.Quote[
                                      `Quote1Discount${capitalizeStr(priceType)}`
                                    ]
                                  }
                                  disabled={!data.Quote.QuoteTypeId1}
                                  onChange={(e) =>
                                    onChangeQuoteDiscountTotal(
                                      e,
                                      `Quote1Discount${capitalizeStr(priceType)}`
                                    )
                                  }
                                />
                              </div>
                            </td>
                            <td className="text-right">
                              <div className="flex-container">
                                <select
                                  className="form-control discount-type-select"
                                  defaultValue={
                                    data.Quote[
                                      `Quote2DiscountType${capitalizeStr(priceType)}`
                                    ]
                                  }
                                  disabled={!data.Quote.QuoteTypeId2}
                                  onChange={(e) =>
                                    onChangeQuoteDiscountTotal(
                                      e,
                                      `Quote2DiscountType${capitalizeStr(priceType)}`
                                    )
                                  }
                                >
                                  <option value="DOLLAR">$</option>
                                  <option value="PERCENT">%</option>
                                </select>
                                <input
                                  type="number"
                                  className="form-control discount-input text-right"
                                  defaultValue={
                                    data.Quote[
                                      `Quote2Discount${capitalizeStr(priceType)}`
                                    ]
                                  }
                                  disabled={!data.Quote.QuoteTypeId2}
                                  onChange={(e) =>
                                    onChangeQuoteDiscountTotal(
                                      e,
                                      `Quote2Discount${capitalizeStr(priceType)}`
                                    )
                                  }
                                />
                              </div>
                            </td>
                            <td colSpan={4}></td>
                          </tr>
                        )}
                        {!groupItemsByCategory && (
                          <tr className="tr-total">
                            <td colSpan={4}>
                              <span className="text-capitalize">{priceType}</span> Total{' '}
                              <small>(After Discount)</small>
                            </td>
                            <td className="text-right">
                              {formatPrice(calculateQuoteTotals(1)[priceType].total)}
                            </td>
                            <td className="text-right">
                              {formatPrice(calculateQuoteTotals(2)[priceType].total)}
                            </td>
                            <td colSpan={4}></td>
                          </tr>
                        )}
                        <tr className="tr-total-margin">
                          <td colSpan={4}>
                            <span className="text-capitalize">Profit Margin</span>
                          </td>
                          <td className="text-right">
                            {formatMargin(calculateQuoteTotals(1)[priceType].total, calculateQuoteCosts()[priceType])}
                          </td>
                          <td className="text-right">
                            {formatMargin(calculateQuoteTotals(2)[priceType].total, calculateQuoteCosts()[priceType])}
                          </td>
                          <td colSpan={4}></td>
                        </tr>
                        {provided.placeholder}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </CardBody>
      </Card>
    );
  };

  const renderSummary = () => {
    if (!data || !quoteItems) return null;
    const [q1, q2] = calculateContractMargins();
    const q1IsNone = !data.Quote.QuoteTypeId1;
    const q2IsNone = !data.Quote.QuoteTypeId2;
    const q1ShowTermNote = !q1IsNone && !data.Quote.QuoteContractMonths1;
    const q2ShowTermNote = !q2IsNone && !data.Quote.QuoteContractMonths2;
    return (
      <Card>
        <CardBody>
          <h5 className="mt-0 mb-3">Quote Comparison</h5>
          <table className="table" id="table-summary">
            <thead>
              <tr>
                <th className="text-right" style={{ width: '30%' }}>
                  Quote 1 {!q1IsNone && quoteBadge(data.Quote.QuoteType1?.Name)}
                </th>
                <th className="text-center" style={{ width: '40%' }}></th>
                <th className="text-left" style={{ width: '30%' }}>
                  Quote 2 {!q2IsNone && quoteBadge(data.Quote.QuoteType2?.Name)}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="text-right">{!q1IsNone && <>{q1.contractPeriod} months{q1ShowTermNote && <span className="text-danger">*</span>}</>}</td>
                <td className="text-center">Contract period</td>
                <td>{!q2IsNone && <>{q2.contractPeriod} months{q2ShowTermNote && <span className="text-danger">*</span>}</>}</td>
              </tr>
              <tr>
                <td className="text-right">{!q1IsNone && formatPrice(q1.totalExpenses)}</td>
                <td className="text-center">Total cost to Switch</td>
                <td>{!q2IsNone && formatPrice(q2.totalExpenses)}</td>
              </tr>
              <tr>
                <td className="text-right">{!q1IsNone && formatPrice(q1.initialRevenue)}</td>
                <td className="text-center">Initial cost to customer</td>
                <td>{!q2IsNone && formatPrice(q2.initialRevenue)}</td>
              </tr>
              <tr>
                <td className="text-right">{!q1IsNone && formatPrice(q1.monthlyRevenue)}</td>
                <td className="text-center">Monthly cost to customer</td>
                <td>{!q2IsNone && formatPrice(q2.monthlyRevenue)}</td>
              </tr>
              <tr>
                <td className="text-right">{!q1IsNone && formatPrice(q1.totalRevenue)}</td>
                <td className="text-center">Total revenue</td>
                <td>{!q2IsNone && formatPrice(q2.totalRevenue)}</td>
              </tr>
              <tr>
                <td className="text-right">{!q1IsNone && q1.payback}</td>
                <td className="text-center">Payback period</td>
                <td>{!q2IsNone && q2.payback}</td>
              </tr>
              <tr>
                <td className={`text-right${!q1IsNone ? ` ${q1.marginPct >= targetQuoteMargin ? 'margin-good' : q1.marginPct >= minimumQuoteMargin ? 'margin-fair' : 'margin-poor'}` : ''}`}>
                  {!q1IsNone && `${formatPrice(q1.margin)} (${q1.marginPct}%)`}
                </td>
                <td className="text-center">Profit margin</td>
                <td className={!q2IsNone ? (q2.marginPct >= targetQuoteMargin ? 'margin-good' : q2.marginPct >= minimumQuoteMargin ? 'margin-fair' : 'margin-poor') : ''}>
                  {!q2IsNone && `${formatPrice(q2.margin)} (${q2.marginPct}%)`}
                </td>
              </tr>
            </tbody>
          </table>
          {(q1ShowTermNote || q2ShowTermNote) && (
            <small className="d-block text-center mt-3">
              <span className="text-danger">*</span> Figures assume a contract period of {capexAssumedContractLength} months
            </small>
          )}
        </CardBody>
      </Card>
    );
  };

  useEffect(() => {
    if (data && data.Quote && data.Bundles && data.ProductsByCategory) {
      if (!['QUOTE_ITEMS', 'CUSTOMER_ACCEPTED'].includes(data.Quote.WorkflowStep)) {
        history.replace(getQuoteBuilderURL(data.Quote.WorkflowStep, data.Quote.QuoteId));
        return;
      }

      // disable screen when status is accepted, declined or voided
      const status = data.Quote.Status;
      setDisableQuote(status === 'accepted');

      let tempQuoteItems = data.QuoteItems || [];
      let quoteLineItemsData = [];
      PRICE_TYPES.forEach((type) => {
        let quoteLineItems = tempQuoteItems.filter((item) => {
          return item.PriceType === type;
        });
        quoteLineItems.forEach((item) => {
          quoteLineItems
            .filter((iitem) => {
              return item.LineId !== iitem.LineId && item.Order === iitem.Order;
            })
            .forEach((iitem) => {
              iitem.Order = item.Order + 1;
            });
        });
        quoteLineItemsData.push(...quoteLineItems);
      });

      setQuoteItems(quoteLineItemsData);
      setQuoteSites(data.Quote.QuoteSites || []);

      let groupOptions = [];

      // add bundles data to the live search
      groupOptions.push({
        label: 'Bundles',
        options: data.Bundles.map((bundle) => ({
          value: bundle.BundleId,
          label: bundle.Name,
          description: bundle.Description,
          items: bundle.BundleItems,
        })),
      });

      // add product categories with products data to the live search
      data.ProductsByCategory.forEach((category) =>
        groupOptions.push({
          label: category.Name,
          options: category.Products.map((product) => ({
            label: product.Name,
            value: product.ProductId,
            productCode: product.ProductCode,
            HasProposalTemplate: product.HasProposalTemplate,
            qty: 1,
          })),
        })
      );
      setGroupOptions(groupOptions);
      setQuoteDiscounts({
        Quote1DiscountMonthly: parseFloat(data.Quote.Quote1DiscountMonthly || 0),
        Quote1DiscountTypeMonthly: data.Quote.Quote1DiscountTypeMonthly,
        Quote1DiscountHardware: parseFloat(data.Quote.Quote1DiscountHardware || 0),
        Quote1DiscountTypeHardware: data.Quote.Quote1DiscountTypeHardware,
        Quote1DiscountSetup: parseFloat(data.Quote.Quote1DiscountSetup || 0),
        Quote1DiscountTypeSetup: data.Quote.Quote1DiscountTypeSetup,
        Quote2DiscountMonthly: parseFloat(data.Quote.Quote2DiscountMonthly || 0),
        Quote2DiscountTypeMonthly: data.Quote.Quote2DiscountTypeMonthly,
        Quote2DiscountHardware: parseFloat(data.Quote.Quote2DiscountHardware || 0),
        Quote2DiscountTypeHardware: data.Quote.Quote2DiscountTypeHardware,
        Quote2DiscountSetup: parseFloat(data.Quote.Quote2DiscountSetup || 0),
        Quote2DiscountTypeSetup: data.Quote.Quote2DiscountTypeSetup,
      });
      setSummariseAllHardwareItemsQuote1(data.Quote.SummariseAllHardwareItemsQuote1 ?? 0);
      setSummariseAllHardwareItemsQuote2(data.Quote.SummariseAllHardwareItemsQuote2 ?? 0);
    }
  }, [data, PRICE_TYPES, history]);

  return (
    <React.Fragment>
      {disableQuote && <div className="overlay"></div>}
      {stepError &&
        stepError.error === true &&
        stepError.marginError && (
          <SweetAlert
            error
            title="Error"
            onConfirm={(e) => setStepError({ error: false, message: '' })}
            confirmBtnText={'Revise quote'}
            showCancel={true}
            onCancel={updateQuoteStep}
            cancelBtnText={'Ignore and continue'}
            cancelBtnBsStyle="secondary"
            closeOnClickOutside={false}
          >
            {stepError.message}
          </SweetAlert>
        )}
      {
        // load live search and quote item builder UI when quote with the ID passed on the URL exists
        data && data.Quote && !loading && (
          <div className="content">
            <div className="container-fluid">
              <div id="wrapper">
                <div className="topbar product-search-bar">
                  <div className="navbar-custom product-search">
                    <h5
                      className="mt-0"
                      style={{ fontSize: '18px' }}
                    >
                      {data.Quote.Description} - Quote Items
                    </h5>
                    {groupOptions && (
                      <Select
                        options={groupOptions}
                        onChange={selectProduct}
                        value={selectedProduct}
                        placeholder={addingToQuote ? 'Adding...' : 'Add a product or bundle...'}
                        isDisabled={addingToQuote}
                        filterOption={(option, inputValue) => {
                          const lower = inputValue.toLowerCase();
                          return option.label.toLowerCase().includes(lower) ||
                            option.data.productCode?.toLowerCase().includes(lower);
                        }}
                        formatOptionLabel={(opt, { context }) => (
                          <>
                            <div>{opt.label}</div>
                            {context === 'menu' && opt.description && <div style={{ fontSize: '0.8em', opacity: 0.8 }}>{opt.description}</div>}
                          </>
                        )}
                      />
                    )}
                  </div>
                </div>
              </div>

              <Row style={{ paddingTop: '150px' }}>
                <Col lg="12">
                  {PRICE_TYPES.map(renderTables)}
                  {renderSummary()}
                </Col>
              </Row>

              <Modal isOpen={siteModal.open} toggle={() => setSiteModal({ ...siteModal, open: false })}>
                <ModalHeader toggle={() => setSiteModal({ ...siteModal, open: false })}>
                  {siteModal.mode === 'create' ? 'Add New Site' : `Rename Site`}
                </ModalHeader>
                <ModalBody>
                  <Form onSubmit={handleSiteModalSave}>
                    <FormGroup>
                      <Label>Site Name <small className="text-danger">*</small></Label>
                      <Input
                        type="text"
                        value={siteModal.name}
                        onChange={(e) => setSiteModal({ ...siteModal, name: e.target.value })}
                        autoFocus
                      />
                    </FormGroup>
                  </Form>
                </ModalBody>
                <ModalFooter>
                  <Button color="secondary" onClick={() => setSiteModal({ ...siteModal, open: false })}>Cancel</Button>
                  <Button color="success" disabled={!siteModal.name.trim()} onClick={handleSiteModalSave}>Save</Button>
                </ModalFooter>
              </Modal>

              <Modal isOpen={!!itemModal} toggle={() => setItemModal(null)}>
                <ModalHeader toggle={() => setItemModal(null)}>
                  {itemModal?.mode === 'add' ? 'Add Custom Item' : 'Edit Quote Item'}
                </ModalHeader>
                <ModalBody>
                  <Form onSubmit={handleItemModalSave}>
                    <FormGroup>
                      <Label>Item Name <small className="text-danger">*</small></Label>
                      <Input
                        type="text"
                        value={itemForm.Description}
                        onChange={(e) => setItemForm({ ...itemForm, Description: e.target.value })}
                      />
                    </FormGroup>
                    {(itemModal?.mode === 'add' || !itemModal?.item?.ProductId) && (
                      <FormGroup>
                        <Label>Product Code</Label>
                        <Input
                          type="text"
                          value={itemForm.ProductCode}
                          onChange={(e) => setItemForm({ ...itemForm, ProductCode: e.target.value })}
                        />
                      </FormGroup>
                    )}
                    {groupItemsByCategory && (
                      <FormGroup>
                        <Label>Category</Label>
                        <Select
                          options={categoryOptions}
                          value={categoryOptions.find(o => o.value === itemForm.CatId)}
                          placeholder="None"
                          isClearable
                          isSearchable={false}
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          onChange={(selected) => setItemForm({ ...itemForm, CatId: selected ? selected.value : null })}
                        />
                      </FormGroup>
                    )}
                  </Form>
                </ModalBody>
                <ModalFooter>
                  <Button color="secondary" onClick={() => setItemModal(null)}>Cancel</Button>
                  <Button
                    color="success"
                    disabled={!itemForm.Description.trim()}
                    onClick={handleItemModalSave}
                  >
                    {itemModal?.mode === 'add' ? 'Add to Quote' : 'Save'}
                  </Button>
                </ModalFooter>
              </Modal>

              <Row>
                <Col lg="12">
                  <Card>
                    <CardBody>
                      <button
                        className="btn-icon btn btn-light btn-lg float-left"
                        onClick={goToPreviousStep}
                      >
                        {' '}
                        <span className="btn-icon-label">
                          <i className="ion ion-md-arrow-back mr-2"></i>
                        </span>{' '}
                        Previous Step
                      </button>
                      <button
                        className="btn-icon btn btn-primary btn-lg float-right"
                        onClick={proceedNextStep}
                      >
                        {' '}
                        <span className="btn-icon-label">
                          <i className="ion ion-md-arrow-forward mr-2"></i>
                        </span>{' '}
                        Next Step
                      </button>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        )
      }
      {
        // when data is loaded and quote id is not valid show quote does not exists error UI
        data && data.Quote === null && !loading && (
          <div className="content">
            <div className="container-fluid">
              <div className="page-title-box">
                <Row className="align-items-center">
                  <Col sm="6">
                    <h4 className="page-title">Error loading this page</h4>
                  </Col>
                </Row>
              </div>

              <Row>
                <Col lg="12">
                  <Card>
                    <CardBody>
                      Quote with id {props.quoteId} does not exist.
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        )
      }
    </React.Fragment>
  );
};

export default connect(null, { activateAuthLayout })(QuoteBuilderItems);
